// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Progress,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import { PERFECT_SCROLLBAR_MAX_220 } from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardAnalytics.json";

import "react-table/react-table.css";

function AnalyticsDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const pagesColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "analytics.col.path" }),
        accessor: "path",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      {
        Header: formatMessage({ id: "analytics.col.views" }),
        accessor: "views",
        Cell: p => addCommas(p.value)
      },
      {
        Header: formatMessage({ id: "analytics.col.exit" }),
        accessor: "exitRate",
        Cell: p => `${p.value}%`
      }
    ];
    }, [intl]);

  const eventsColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "analytics.col.event" }),
        accessor: "name",
        Cell: p => <code className="small">{p.value}</code>
      },
      {
        Header: formatMessage({ id: "analytics.col.count" }),
        accessor: "count",
        Cell: p => addCommas(p.value)
      }
    ];
    }, [intl]);

  const funnelOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "80%",
          distributed: true,
          borderRadius: 4
        }
      },
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4,
        c.ReactifyChartColor5
      ],
      dataLabels: {
        enabled: true,
        formatter: (val, ctx) => {
          const i = ctx.dataPointIndex;
          if (i === 0) return addCommas(val);
          const prev = data.funnel[i - 1].value;
          const pct = Math.round((val / prev) * 1000) / 10;
          return `${addCommas(val)} (${pct}%)`;
        }
      },
      xaxis: {
        categories: data.funnel.map(f => f.step),
        labels: { style: { colors: c.primaryColor } }
      },
      grid: { borderColor: c.separatorColor },
      legend: { show: false },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const channelOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: [c.ReactifyChartColor2],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.channels.map(ch => ch.name),
        labels: {
          style: { colors: c.primaryColor },
          rotate: -25,
          rotateAlways: true
        }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const trendOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 2], curve: "smooth" },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor3],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.trend.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const deviceOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.devices.map(d => d.name),
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor2, c.ReactifyChartColor4],
      legend: { position: "bottom" },
      plotOptions: { pie: { donut: { size: "70%" } } },
      dataLabels: { enabled: true }
    };
    }, [colors]);

  const { formatMessage } = intl;
  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--analytics">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.analytics" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-2">
            <Colxx md="6" lg="3" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-0 small">
                        <IntlMessages id="analytics.realtime.active" />
                      </p>
                      <h3 className="mb-0">
                        {addCommas(data.realtime[0].value)}
                      </h3>
                    </div>
                    <Button color="primary" size="sm" outline>
                      <IntlMessages id="analytics.realtime.live" />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="6" lg="3" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3">
                  <p className="text-muted mb-0 small">
                    <IntlMessages id="analytics.realtime.events" />
                  </p>
                  <h3 className="mb-0">{data.realtime[1].value}</h3>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.sessionSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="analytics.kpi.sessions" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.sessions)}</h4>
                  <span className="text-success small">+{k.sessionsDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-users text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="analytics.kpi.users" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.users)}</h4>
                  <span className="text-success small">+{k.usersDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-sign-out text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="analytics.kpi.bounce" />
                  </p>
                  <h4 className="mb-0">{k.bounceRate}%</h4>
                  <span className="text-success small">{k.bounceDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-clock-o text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="analytics.kpi.session-time" />
                  </p>
                  <h4 className="mb-0">{k.avgSessionSec}s</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-bullseye text-danger mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="analytics.kpi.conversions" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.conversions)}</h4>
                  <span className="text-muted small">
                    {k.conversionRate}% CVR
                  </span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-eye text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="analytics.kpi.pageviews" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.pageviews)}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-filter text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="analytics.kpi.funnel-start" />
                  </p>
                  <h4 className="mb-0">
                    {addCommas(data.funnel[0].value)}
                  </h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-check-circle text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="analytics.kpi.funnel-paid" />
                  </p>
                  <h4 className="mb-0">
                    {addCommas(data.funnel[data.funnel.length - 1].value)}
                  </h4>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.chart.funnel" />
                  </CardTitle>
                  <Chart
                    options={funnelOptions}
                    series={[{ data: data.funnel.map(f => f.value) }]}
                    type="bar"
                    height={360}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.chart.channels" />
                  </CardTitle>
                  <Chart
                    options={channelOptions}
                    series={[
                      { name: "Sessions", data: data.channels.map(c => c.sessions) }
                    ]}
                    type="bar"
                    height={360}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="8" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.chart.trend" />
                  </CardTitle>
                  <Chart
                    options={trendOptions}
                    series={[
                      { name: "Sessions (k)", data: data.trend.sessions },
                      { name: "Events (k)", data: data.trend.events }
                    ]}
                    type="line"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.chart.devices" />
                  </CardTitle>
                  <Chart
                    options={deviceOptions}
                    series={data.devices.map(d => d.pct)}
                    type="donut"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.section.retention" />
                  </CardTitle>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0 text-center">
                      <thead>
                        <tr>
                          <th />
                          {data.retention.weeks.map(w => (
                            <th key={w}>{w}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.retention.cohorts.map((row, ri) => (
                          <tr key={ri}>
                            <td className="text-left font-weight-medium">
                              {row.label}
                            </td>
                            {row.values.map((v, vi) => (
                              <td key={vi}>
                                {v == null ? (
                                  "—"
                                ) : (
                                  <Badge
                                    color={
                                      v >= 40
                                        ? "success"
                                        : v >= 30
                                          ? "warning"
                                          : "secondary"
                                    }
                                  >
                                    {v}%
                                  </Badge>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="6" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.section.goals" />
                  </CardTitle>
                  {data.goals.map((g, i) => {
                    const pct = Math.min(100, (g.actual / g.target) * 100);
                    return (
                      <div key={i} className="mb-4">
                        <div className="d-flex justify-content-between mb-1">
                          <span>{g.name}</span>
                          <span className="text-muted small">
                            {addCommas(g.actual)} / {addCommas(g.target)}
                          </span>
                        </div>
                        <Progress value={pct} />
                      </div>
                    );
                  })}
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.table.pages" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.topPages}
                    columns={pagesColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.table.events" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.topEvents}
                    columns={eventsColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx xxs="12" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="analytics.section.live-stream" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_220}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>{formatMessage({ id: "analytics.col.time" })}</th>
                          <th>{formatMessage({ id: "analytics.col.event" })}</th>
                          <th>{formatMessage({ id: "analytics.col.detail" })}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.liveEvents.map((e, i) => (
                          <tr key={i}>
                            <td className="text-muted text-nowrap">{e.t}</td>
                            <td>
                              <code className="small">{e.event}</code>
                            </td>
                            <td>{e.detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default AnalyticsDashboard;
